import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  GRAVES_QUERY,
  GRAVES_COUNT_QUERY,
  PUBLICATIONS_QUERY,
  SITES_QUERY
} from '../graphql/queries';
import usePaginatedList from '../hooks/usePaginatedList';
import PageFilters from '../components/PageFilters';
import SitesMap from '../components/SitesMap';
import PaginationControls from '../components/PaginationControls';
import DataTable from '../components/DataTable';
import QueryError from '../components/QueryError';
import M3LinearProgress from '../components/M3Progress';
import TableActions from '../components/TableActions';

// Mimics FiguresHelper#number_with_unit: 2 decimals, cm for sub-meter "m" values
function formatWithUnit(n) {
  if (!n || n.value == null) return '—';
  if (n.value < 0.1 && n.unit === 'm') {
    return `${(n.value * 100).toFixed(2)} cm`;
  }
  return `${n.value.toFixed(2)} ${n.unit}`;
}

export default function GravesPage() {
  const navigate = useNavigate();
  const { data: publicationsData } = useQuery(PUBLICATIONS_QUERY);
  const publications =
    publicationsData?.publications?.map((p) => ({ id: p.id, label: `${p.author} ${p.year}` })) ||
    [];

  const { data: sitesData } = useQuery(SITES_QUERY);
  const sites = sitesData?.sites || [];

  const {
    filters,
    setFilters,
    page,
    setPage,
    sort,
    setSort,
    handleFilterChange,
    listData,
    loading,
    error,
    total,
    totalPages
  } = usePaginatedList(GRAVES_QUERY, GRAVES_COUNT_QUERY, { baseVars: {} });

  // Deep link: ?siteId=<id> (from the sites map) seeds the site filter.
  const [searchParams] = useSearchParams();
  const urlSiteId = searchParams.get('siteId');
  useEffect(() => {
    setFilters((prev) => {
      const next = { ...prev };
      if (urlSiteId) next.siteId = urlSiteId;
      else delete next.siteId;
      return next;
    });
  }, [urlSiteId, setFilters]);

  const handleSort = (columnKey) => {
    const [key, direction] = sort.split(':');
    const newDirection = key === columnKey && direction === 'asc' ? 'desc' : 'asc';
    setSort(`${columnKey}:${newDirection}`);
  };

  const columns = [
    {
      key: 'identifier',
      title: 'ID',
      numeric: true,
      render: (v) => v || '—'
    },
    {
      key: 'publication',
      title: 'Publication',
      render: (v) => (v ? `${v.author} ${v.year}` : '—')
    },
    {
      key: 'site',
      title: 'Site',
      render: (v) => v?.name || '—'
    },
    {
      key: 'areaWithUnit',
      title: 'Area',
      numeric: true,
      render: (v) => formatWithUnit(v)
    },
    {
      key: 'perimeterWithUnit',
      title: 'Perimeter',
      numeric: true,
      render: (v) => formatWithUnit(v)
    },
    {
      key: 'width',
      title: 'Width',
      numeric: true,
      render: (v, row) => formatWithUnit(row.widthWithUnit)
    },
    {
      key: 'height',
      title: 'Length',
      numeric: true,
      render: (v, row) => formatWithUnit(row.heightWithUnit)
    },
    {
      key: 'tags',
      title: 'Tags',
      render: (v) => v?.map((t) => t.name).join(' ') || '—'
    },
    {
      key: 'orientation',
      title: 'Orientation',
      numeric: true,
      render: (v, row) => {
        if (row?.arrow?.angle == null) return '—';
        const graveAngle = Math.round(Math.abs(row.angle || 0));
        const arrowAngle = row.arrow.angle;
        return `${Math.round((graveAngle + arrowAngle) % 180)}°`;
      }
    }
  ];

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );

  return (
    <div className="page-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Graves</h1>
        <PageFilters
          filters={filters}
          handleFilterChange={handleFilterChange}
          publications={publications}
          sites={sites}
        />
      </div>

      {loading && <M3LinearProgress indeterminate />}

      <SitesMap selectedId={filters.siteId} />

      <DataTable
        columns={columns}
        data={listData}
        loading={loading}
        rowKey="id"
        onRowClick={(grave) => navigate(`/graves/${grave.id}`)}
        emptyMessage="No graves found"
        sortConfig={{ key: sort.split(':')[0], direction: sort.split(':')[1] }}
        onSort={handleSort}
        actions={(grave) => (
          <TableActions
            items={[
              { icon: 'account_tree', label: 'Relations', to: `/graves/${grave.id}/related` },
              { icon: 'edit', label: 'Edit', to: `/graves/${grave.id}/edit` }
            ]}
          />
        )}
      />
      <PaginationControls
        page={page}
        totalPages={totalPages}
        total={total}
        totalLabel="grave"
        setPage={setPage}
      />
    </div>
  );
}
